import { useEffect, useMemo } from 'react'
import { Outlet, useNavigate, useParams } from 'react-router-dom'

import { useAuthStore } from '@/features/auth'
import DeleteDocumentDialog from '@/features/document/components/DeleteDocumentDialog'
import { FloatingUploadIndicator } from '@/features/file-upload'
import {
  NotificationDrawer,
  useNotificationDrawerStore,
} from '@/features/notification'
import { useSidebarStore } from '@/features/sidebar'
import { ERoleType } from '@/features/user-management/constants'
import { TFLSidebar } from '@/features/view-tfl-uploaded/components/TFLSidebar'
import { cn } from '@/utils'
import { ROUTES } from '@/utils/routes'

import Sidebar from '../Sidebar'

export const TFLViewerLayout = () => {
  const getListDeliverableSetUserHasAccess = useAuthStore(
    (state) => state.getListDeliverableSetUserHasAccess
  )
  const roles = useAuthStore((state) => state.roles)
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const deliverableSetIds = getListDeliverableSetUserHasAccess()
  const hasAccess = deliverableSetIds.includes(id || '')

  const { setSidebarCollapsed } = useSidebarStore()

  const hasOrgAdminRole = useMemo(
    () =>
      roles?.some(
        (role) =>
          role.role_name === ERoleType.ORGANIZATION_ADMIN ||
          role.role_name === ERoleType.PROJECT_LEAD ||
          role.role_name === ERoleType.TEAM_MEMBER
      ),
    [roles]
  )

  const {
    isOpen: isNotificationDrawerOpen,
    isPinned: isNotificationDrawerPinned,
  } = useNotificationDrawerStore()

  useEffect(() => {
    // Skip access restriction for orgAdmin users
    if (hasOrgAdminRole) return

    if (!hasAccess) {
      navigate(ROUTES.UNAUTHORIZED, { replace: true })
    }
  }, [hasOrgAdminRole, hasAccess, navigate])

  useEffect(() => {
    setSidebarCollapsed(true)
  }, [setSidebarCollapsed])

  return (
    <div className="relative flex flex-col h-screen overflow-hidden">
      {/* Body - Sidebar + Content */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left Panel */}
        <Sidebar />

        <div
          className={cn(
            'transition-all duration-300',
            isNotificationDrawerOpen && isNotificationDrawerPinned
              ? 'w-notification-drawer'
              : 'w-0'
          )}
        />
        {/* Left Sidebar - Fixed width */}
        <aside className="z-20 relative flex-shrink-0">
          <TFLSidebar />
        </aside>

        {/* Main Content Area - Relative positioning for overlay */}
        <main className="z-10 relative flex-1 bg-quaternary overflow-hidden">
          <Outlet />
        </main>
      </div>
      <DeleteDocumentDialog />

      {/* Floating Upload Indicator - shown during uploads when not on upload page */}
      <FloatingUploadIndicator />

      {/* Notification Drawer - shown when notification icon is clicked in sidebar */}
      <NotificationDrawer />
    </div>
  )
}
